import React, { useState, useEffect, useContext, useMemo } from 'react';
import { StoreContext } from './Store';
import { Input } from '../legacy-components/Input/Input';
import {
  getCommcodeRefreshTable,
  getUpdateCommcodeSingle,
  getUpdateCommcodeAll,
  delCommcodeSingle,
  putCommcodeSingle,
} from './apiCalls';
import { Container, Row, Col } from 'react-bootstrap';
import DataTable from './DataTable';
import ConfirmationModal from './ConfirmationModal';

const ValidationError = () => <div className="text-danger small">Please enter a 10-digit commodity code.</div>;

const UserAdminCommcodes = () => {
  const [requestFailed, setRequestFailed] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [commodityCode, setCommodityCode] = useState('');
  const [delCommCode, setDelCommCode] = useState('');
  const [resCommCode, setResCommCode] = useState('');
  const [refreshData, setRefreshData] = useState([]);
  const [fetching, setFetching] = useState(false);
  const [error, setError] = useState('');

  const store = useContext(StoreContext);

  const minWidthValue = '1500px';

  const fetchData = async () => {
    try {
      const data = await getCommcodeRefreshTable();
      setRefreshData(data);
    } catch (err) {
      console.log(err);
      setRequestFailed(true);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleOneCommcodeFetch = async () => {
    try {
      setFetching(true);
      const data = await getUpdateCommcodeSingle(commodityCode);
      await fetchData();
      setFetching(false);
    } catch (e) {
      setError(e);
    }
  };
  const handleDelCommcodeFetch = async () => {
    try {
      setFetching(true);
      const data = await delCommcodeSingle(delCommCode);
      await fetchData();
      setFetching(false);
    } catch (e) {
      setError(e);
    }
  };
  const handleRestoreCommcodeFetch = async () => {
    try {
      setFetching(true);
      const data = await putCommcodeSingle(resCommCode);
      await fetchData();
      setFetching(false);
    } catch (e) {
      setError(e);
    }
  };
  const handleAllCommcodeFetch = async () => {
    try {
      setFetching(true);
      const data = await getUpdateCommcodeAll();
      await fetchData();
      setFetching(false);
    } catch (e) {
      setError(e);
    }
  };

  const fetchingInProgress = useMemo(() => {
    // if a job is in progress and started within the last 2 hours, consider it still in progress
    // otherwise it's likely stuck (i.e. did not complete successfully) and we should allow the user to start a new fetch
    const twoHoursAgo = new Date(Date.now() - 2 * 60 * 60 * 1000);
    return refreshData.some((d) => d.status === 1 && new Date(d.startTime) > twoHoursAgo);
  });

  if (requestFailed) return <p>Failed!</p>;
  return (
    <Container className="teamBackground" style={{ minWidth: minWidthValue }}>
      {showConfirmModal && (
        <ConfirmationModal
          header={'Fetch All Commcodes?'}
          show={showConfirmModal}
          onHide={(event) => {
            if (event?.target?.id === 'confirm') {
              handleAllCommcodeFetch();
            }
            setShowConfirmModal(false);
          }}
          onConfirm={() => {}}
          hideCancel={false}
          animation={true}
        >
          <p>
            This Operation will fetch all of the commcodes and may take several hours to complete. This screen will be
            disabled while fetching commcodes.
          </p>
        </ConfirmationModal>
      )}
      {/* Unsure where the below classname comes from, doesn't seem to effect code */}
      <Row className="background ">
        <Col className="teamHeader ">
          <div className="d-flex justify-content-between">
            <span className="teamHeaderText">Commcode Refresh</span>
          </div>
        </Col>

        {fetchingInProgress ? (
          <div style={{ marginBottom: '30px' }}>
            Fetching is currently in progress. Please return to this screen once fetching has been completed.
          </div>
        ) : (
          <Row style={{ marginTop: '30px', marginBottom: '30px' }}>
            <Col>
              <span className="section-title">Refresh One Commcode</span>
              <div className="d-flex flex-row">
                <Input type="number" value={commodityCode} onChange={(e) => setCommodityCode(e.target.value)} />
                <button
                  className="blue-button-teams"
                  style={{ marginLeft: '10px' }}
                  disabled={fetching || commodityCode.length !== 10}
                  onClick={handleOneCommcodeFetch}
                >
                  Refresh
                </button>
              </div>
              {commodityCode && commodityCode.length !== 10 && <ValidationError />}
            </Col>
            <Col>
              <span className="section-title">Soft Delete One Commcode</span>
              <div className="d-flex flex-row">
                <Input type="number" value={delCommCode} onChange={(e) => setDelCommCode(e.target.value)} />
                <button
                  className="blue-button-teams red text-white"
                  style={{ marginLeft: '10px' }}
                  disabled={fetching || delCommCode.length !== 10}
                  onClick={handleDelCommcodeFetch}
                >
                  Delete
                </button>
              </div>
              {delCommCode && delCommCode.length !== 10 && <ValidationError />}
            </Col>

            <Col>
              <span className="section-title">Restore One Commcode</span>
              <div className="d-flex flex-row">
                <Input type="number" value={resCommCode} onChange={(e) => setResCommCode(e.target.value)} />
                <button
                  className="blue-button-teams green-btn"
                  style={{ marginLeft: '10px' }}
                  disabled={fetching || resCommCode.length !== 10}
                  onClick={handleRestoreCommcodeFetch}
                >
                  Restore
                </button>
              </div>
              {resCommCode && resCommCode.length !== 10 && <ValidationError />}
            </Col>
          </Row>
        )}

        <Row className="adminTable">
          <h4>Refresh History</h4>
          <DataTable
            data={refreshData}
            sortable
            filterable
            dontRefreshPagination
            className="table-striped table-bordered"
          >
            <DataTable.Pagination itemsPerPage={[10, 25, 50]} style={{ fontSize: '4px' }} />
            <DataTable.Headers style={{ backgroundColor: 'white', fontSize: '13px' }} />
            <DataTable.Column colKey="commoditycode" header="Commodity Code" />
            <DataTable.Column colKey="startTime" header="Start Time" />
            <DataTable.Column colKey="endTime" header="End Time" />
            <DataTable.Column colKey="action" header="Action" />
            <DataTable.Column colKey="status" header="Status">
              {({ status }) => {
                if (status === 1) return 'In Progress';
                else if (status === 2) return 'Completed';
                else return 'Error';
              }}
            </DataTable.Column>
          </DataTable>
        </Row>
      </Row>
    </Container>
  );
};

export default UserAdminCommcodes;
